import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { setColor } from "../../../assets/styles/styles";
import X from "../../../assets/images/buttonIcons/purpleX.png";
import { hexToRGBA } from "../../../utils/generalUtils";
import MobileAccountBackground from "../../../assets/images/backgrounds/UC_Mobile_BG.png";
import IntraverseMiniLogoIcon from "../user/profile/images/ColoredIntraverseLogo.png";
import Chevron from "../../../assets/images/buttonIcons/Chevron.png";

const Modal = ({ setIsOpen, isOpen, children, closeButton = true, maxHeight, width, height, overflowY, padding }) => {
    const handleClose = () => setIsOpen(false);
    const isMobile = window.innerWidth <= 768;
    const modalRef = useRef(null);
    let scrollTimeout = null;

    useEffect(() => {
        const handleScroll = () => {
            if (modalRef.current) {
                modalRef.current.classList.add("scrolling");
                if (scrollTimeout) {
                    clearTimeout(scrollTimeout);
                }
                scrollTimeout = setTimeout(() => {
                    if (modalRef.current) {
                        modalRef.current.classList.remove("scrolling");
                    }
                }, 500);
            }
        };

        const modalElement = modalRef.current;
        if (modalElement) {
            const isScrollable = modalElement.scrollHeight > modalElement.clientHeight;
            if (isScrollable) {
                modalElement.addEventListener("scroll", handleScroll);
            } else {
                console.warn("ModalContent is not scrollable. Ensure content exceeds max-height.");
            }
        }

        return () => {
            if (modalElement) {
                modalElement.removeEventListener("scroll", handleScroll);
            }
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
        };
    }, []);

    return (
        <>
            {isOpen && (
                <>
                    <ModalContainer>
                        {isMobile && (
                            <ModalNavigation>
                                <ModalTopNav isCenter>
                                    <PortalHeaderMenu isOpen={isOpen}>
                                        <PortalHeaderLogo
                                            // onClick={() => routeChange("/")}
                                            src={IntraverseMiniLogoIcon}
                                            alt="Intraverse Logo"
                                        />
                                    </PortalHeaderMenu>
                                    <MobileExit>
                                        <MobileChevron src={Chevron} onClick={() => handleClose()} alt="Exit" />
                                    </MobileExit>
                                </ModalTopNav>
                            </ModalNavigation>
                        )}
                        <ModalContent
                            className="modal-content"
                            padding={padding}
                            overflowY={overflowY}
                            maxHeight={maxHeight}
                            height={height}
                            width={width}
                            ref={modalRef}
                        >
                            {closeButton && (
                                <Buttons>
                                    <div>
                                        <CloseIcon src={X} alt="Close" onClick={() => handleClose()} close />
                                    </div>
                                </Buttons>
                            )}
                            <Children>{children}</Children>
                        </ModalContent>
                    </ModalContainer>
                </>
            )}
        </>
    );
};

Modal.propTypes = {
    setIsOpen: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    children: PropTypes.node,
    closeButton: PropTypes.bool,
    width: PropTypes.string,
    height: PropTypes.string,
    maxHeight: PropTypes.string,
    overflowY: PropTypes.string,
};

export default Modal;

const ModalContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${hexToRGBA(setColor.black, 0.5)};
    backdrop-filter: blur(64px);
    -webkit-backdrop-filter: blur(64px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999;

    @media (max-width: 768px) {
        background: url(${MobileAccountBackground}) no-repeat center center fixed;
        flex-direction: column;
        align-items: stretch;
    }
    &.modal-content {
    }
`;

const ModalContent = styled.div`
    border: 2px solid ${setColor.darkPurple50};
    background-color: ${setColor.darkPurple50};
    padding: ${(props) => (props.padding ? props.padding : "25px")};
    border-radius: 10px;
    width: ${(props) => (props.width ? props.width : "700px")};
    height: ${(props) => (props.height ? props.height : "auto")};
    max-height: ${(props) => (props.maxHeight ? props.maxHeight : "80vh")};
    display: flex;
    flex-direction: column;
    overflow-y: ${(props) => (props.overflowY ? props.overflowY : "auto")};
    overflow-x: hidden;
    position: relative;

    @media (max-width: 768px) {
        background-color: transparent;
        max-height: calc(100vh - 60px);
        width: 100%;
        height: 100%;
        overflow-y: auto;
        border-radius: 0;
        justify-content: space-between;
        margin-top: 60px;
    }

    /* Firefox */
    scrollbar-width: none; /* Hide scrollbar by default */
    &.scrolling {
        scrollbar-width: thin; /* Show thin scrollbar when scrolling */
        scrollbar-color: ${hexToRGBA(setColor.accent4, 0.5)} transparent;
    }

    /* Webkit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
        width: 6px; /* Fixed width */
        opacity: 0; /* Hide by default */
        transition: opacity 0.3s;
    }

    &.scrolling::-webkit-scrollbar {
        opacity: 1; /* Show scrollbar when scrolling */
    }

    &::-webkit-scrollbar-track {
        border-radius: 18px;
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: ${hexToRGBA(setColor.accent4, 0.5)};
        border-radius: 10px;
    }
`;

const Buttons = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-direction: row;
    width: 100%;
`;

const Children = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    height: 100%;

    @media (max-width: 768px) {
        justify-content: unset;
    }
`;

const CloseIcon = styled.img`
    position: absolute;
    top: 15px;
    right: 15px;
    height: 20px;
    width: auto;
    cursor: pointer;

    @media (max-width: 960px) {
        margin-bottom: 15px;
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const ModalNavigation = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 100000;
    border-bottom: 1px solid ${setColor.brightVioletTransparent};
`;

const ModalTopNav = styled.div`
    width: 100%;
    display: flex;
    justify-content: ${(props) => (props.isCenter ? "center" : "space-between")};
    align-items: center;
    background-color: transparent !important;
    z-index: 100001;
    height: 60px;
    padding: 0 20px;
`;

const PortalHeaderMenu = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    margin: 0;
    background-color: transparent !important;
`;

const PortalHeaderLogo = styled.img`
    cursor: pointer;
    width: 40px;
    height: 27px;

    @media (min-width: 768px) and (max-width: 1024px) {
        max-width: 80px;
        margin-left: 0rem;
    }
`;

const MobileExit = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    position: absolute;
    left: 0;
    top: 25%;
    width: 40px;
    height: 40px;
    z-index: 99999;
    padding: 10px;
`;

const MobileChevron = styled.div`
    background-image: url(${Chevron});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: 20px;
    height: 20px;
`;
